import { Input as AntInput, InputProps as AntInputProps, Form } from 'antd'

export interface InputProps extends Omit<AntInputProps, 'size'> {
  /**
   * Label text displayed above the input
   */
  label?: string

  /**
   * Error message to display
   */
  error?: string

  /**
   * Helper text displayed below the input
   */
  helperText?: string

  /**
   * Mark the field as required
   */
  required?: boolean

  /**
   * Size of the input
   * @default 'small'
   */
  size?: 'small' | 'medium' | 'large'

  /**
   * Full width input
   */
  block?: boolean
}

/**
 * Input Component
 *
 * A text input field with label, error, and helper text support.
 *
 * @example
 * <Input
 *   label="Email"
 *   placeholder="Enter your email"
 *   required
 * />
 *
 * @example
 * // Input with error
 * <Input
 *   label="Password"
 *   type="password"
 *   error="Password is required"
 * />
 *
 * @example
 * // Input with helper text
 * <Input
 *   label="Username"
 *   helperText="Must be at least 3 characters"
 * />
 */
export function Input({
  label,
  error,
  helperText,
  required,
  size = 'small',
  block = true,
  ...props
}: InputProps) {
  const antSize = size === 'medium' ? 'middle' : size

  // If there's a label, wrap in Form.Item for consistent styling
  if (label || error || helperText) {
    return (
      <Form.Item
        label={label}
        required={required}
        validateStatus={error ? 'error' : undefined}
        help={error || helperText}
        className="mb-6"
      >
        <AntInput size={antSize} style={block ? { width: '100%' } : undefined} {...props} />
      </Form.Item>
    )
  }

  // Standalone input without label
  return <AntInput size={antSize} style={block ? { width: '100%' } : undefined} {...props} />
}
